import { memo, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { AddBusiness, Refresh } from "@mui/icons-material";
import { useProvidedAffiliations } from "../hooks/useProvidedAffiliations";
import { AddAffiliationDialog } from "./AddAffiliationDialog";
import { AffiliationCard } from "./AffiliationCard";

/**
 * Gestão das organizações que recebem a teleconsultoria da organização logada.
 * Container do domínio: usa o hook do módulo e não recebe props da página.
 */
export const TeleconsultoriaAffiliationsPanel = memo(
  function TeleconsultoriaAffiliationsPanel() {
    const {
      rows,
      isLoading,
      isCreating,
      pendingRowId,
      reload,
      addReceiver,
      setRowActive,
      answerRequest,
    } = useProvidedAffiliations();
    const [isDialogOpen, setIsDialogOpen] = useState(false);

    // Só as já listadas (desabilitadas na busca); a própria organização a busca já remove.
    const blockedTokens = useMemo(
      () => new Set(rows.map((row) => row.receiverToken)),
      [rows],
    );
    const activeCount = rows.filter((row) => row.isActive).length;
    const requestCount = rows.filter((row) => row.isPendingRequest).length;

    const addButton = (
      <Button
        variant="contained"
        startIcon={<AddBusiness />}
        onClick={() => setIsDialogOpen(true)}
        sx={{ textTransform: "none", width: { xs: "100%", sm: "auto" } }}
      >
        Adicionar organização
      </Button>
    );

    return (
      <Stack spacing={2} width="100%">
        {/* ---- Cabeçalho: resumo + ações principais sempre visíveis ---- */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems={{ xs: "stretch", sm: "center" }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Organizações atendidas
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {activeCount} ativa{activeCount === 1 ? "" : "s"}
              {requestCount
                ? ` · ${requestCount} pedido${requestCount === 1 ? "" : "s"} aguardando aprovação`
                : ""}
              {" · "}essas organizações veem seus teleconsultores e horários.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip title="Atualizar lista">
              <span>
                <IconButton onClick={() => void reload()} disabled={isLoading} aria-label="Atualizar lista">
                  <Refresh />
                </IconButton>
              </span>
            </Tooltip>
            {addButton}
          </Stack>
        </Stack>

        {/* ---- Lista / estados ---- */}
        {isLoading && !rows.length ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress size={28} />
          </Stack>
        ) : rows.length ? (
          <Stack spacing={1} role="list">
            {rows.map((row) => (
              <AffiliationCard
                key={row.id}
                row={row}
                isPending={pendingRowId === row.id}
                isDisabled={Boolean(pendingRowId)}
                onToggleActive={(target, isActive) => void setRowActive(target, isActive)}
                onAnswerRequest={(target, isApproved) => void answerRequest(target, isApproved)}
              />
            ))}
          </Stack>
        ) : (
          <Paper variant="outlined" sx={{ p: 3, textAlign: "center" }}>
            <Stack spacing={1.5} alignItems="center">
              <AddBusiness color="disabled" sx={{ fontSize: 40 }} aria-hidden />
              <Typography variant="body1" fontWeight={600}>
                Nenhuma organização atendida ainda
              </Typography>
              <Typography variant="body2" color="text.secondary" maxWidth={420}>
                Adicione as clínicas que vão receber a teleconsultoria da sua
                organização. Elas poderão agendar com seus teleconsultores.
              </Typography>
              {addButton}
            </Stack>
          </Paper>
        )}

        <AddAffiliationDialog
          isOpen={isDialogOpen}
          isSaving={isCreating}
          blockedTokens={blockedTokens}
          onClose={() => setIsDialogOpen(false)}
          onConfirm={addReceiver}
        />
      </Stack>
    );
  },
);
